if (!customElements.get('shoppable-layout-slider')) {
  class ShoppableLayoutSlider extends HTMLElement {
    constructor() {
      super();

      this.swiper = null;
      this.sectionObserver = null;
      this.videoObserver = null;
      this.visibleVideos = new Set();
      this.videoStates = new WeakMap();

      this.handleSectionLoad = this.observeSection.bind(this);
      this.handleVisibilityChange = this.onVisibilityChange.bind(this);
      this.handlePageShow = this.onPageShow.bind(this);

      this.style.opacity = '0';
      this.style.transition = 'opacity 0.3s ease-out';
    }

    connectedCallback() {
      if (this.isInitializedForPage) return;
      this.isInitializedForPage = true;

      if (window.Shopify && Shopify.designMode) {
        window.addEventListener(
          'shopify:section:load',
          this.handleSectionLoad
        );
      }

      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );

      window.addEventListener('pageshow', this.handlePageShow);

      this.observeSection();
    }

    disconnectedCallback() {
      this.sectionObserver?.disconnect();
      this.videoObserver?.disconnect();

      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );

      window.removeEventListener('pageshow', this.handlePageShow);
      window.removeEventListener(
        'shopify:section:load',
        this.handleSectionLoad
      );

      this.querySelectorAll('video').forEach(video => {
        this.stopVideo(video);
      });

      this.visibleVideos.clear();

      this.swiper?.destroy?.(true, true);
      this.swiper = null;
      this.isInitializedForPage = false;
    }

    observeSection() {
      this.sectionObserver?.disconnect();

      this.sectionObserver = new IntersectionObserver(
        entries => {
          const sectionIsVisible = entries.some(
            entry => entry.isIntersecting
          );

          if (!sectionIsVisible) return;

          this.init();

          this.sectionObserver?.disconnect();
          this.sectionObserver = null;
        },
        {
          threshold: 0.1,
          rootMargin: '100px 0px'
        }
      );

      this.sectionObserver.observe(this);
    }

    init() {
      if (this.swiper) return;

      const swiperContainer = this.querySelector(
        '.swiper-shoppable-slider'
      );

      const isInsideContainerSlider = this.closest(
        '.shoppable-container__slider'
      );

      if (!swiperContainer) {
        this.style.opacity = '1';
        return;
      }

      this.swiper = new Swiper(swiperContainer, {
        autoplay: false,
        spaceBetween: 2,
        slidesPerView: 'auto',

        navigation: {
          nextEl: '.swiper-button--next',
          prevEl: '.swiper-button--prev'
        },

        breakpoints: {
          1200: {
            spaceBetween: isInsideContainerSlider ? 40 : 2,
            slidesPerView: 3.8
          },

          990: {
            spaceBetween: isInsideContainerSlider ? 32 : 2,
            slidesPerView: 3.4
          },

          750: {
            spaceBetween: isInsideContainerSlider ? 24 : 2,
            slidesPerView: 2.4
          },

          360: {
            spaceBetween: isInsideContainerSlider ? 16 : 2,
            slidesPerView: 1.4
          }
        },

        on: {
          init: () => {
            this.style.opacity = '1';
            this.observeVideos();
          }
        }
      });
    }

    observeVideos() {
      this.videoObserver?.disconnect();

      this.videoObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            const video = entry.target;

            const shouldPlay =
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.55 &&
              !document.hidden;

            if (shouldPlay) {
              this.visibleVideos.add(video);
              this.startVideo(video);
            } else {
              this.visibleVideos.delete(video);
              this.stopVideo(video);
            }
          });
        },
        {
          threshold: [0, 0.25, 0.55, 0.75, 1]
        }
      );

      this.querySelectorAll('video').forEach(video => {
        this.prepareVideo(video);
        this.videoObserver.observe(video);
      });
    }

    prepareVideo(video) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.autoplay = false;
      video.preload = 'metadata';
      video.controls = false;

      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.removeAttribute('autoplay');

      if (!this.videoStates.has(video)) {
        this.videoStates.set(video, {
          attempts: 0,
          retryTimer: null,
          readyHandler: null
        });

        video.addEventListener('playing', () => {
          const state = this.getVideoState(video);

          state.attempts = 0;
          video.controls = false;
          video.removeAttribute('data-autoplay-blocked');
        });

        video.addEventListener('error', () => {
          if (this.visibleVideos.has(video)) {
            this.showManualControls(video);
          }
        });
      }
    }

    startVideo(video) {
      if (!this.visibleVideos.has(video) || document.hidden) return;

      this.clearRetry(video);

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.autoplay = true;
      video.controls = false;

      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('autoplay', '');
      video.removeAttribute('data-autoplay-blocked');

      if (video.networkState === video.NETWORK_EMPTY) {
        video.load();
      }

      try {
        const playPromise = video.play();

        if (
          playPromise &&
          typeof playPromise.then === 'function'
        ) {
          playPromise
            .then(() => {
              const state = this.getVideoState(video);

              state.attempts = 0;
              video.controls = false;
              video.removeAttribute('data-autoplay-blocked');
            })
            .catch(error => {
              this.handlePlayFailure(video, error);
            });
        }
      } catch (error) {
        this.handlePlayFailure(video, error);
      }
    }

    handlePlayFailure(video, error) {
      if (!this.visibleVideos.has(video) || document.hidden) return;

      if (
        error?.name === 'NotAllowedError' ||
        error?.name === 'NotSupportedError'
      ) {
        this.showManualControls(video);
        return;
      }

      this.scheduleRetry(video);
    }

    scheduleRetry(video) {
      const state = this.getVideoState(video);

      if (state.attempts >= 2) {
        this.showManualControls(video);
        return;
      }

      state.attempts += 1;

      this.clearRetry(video);

      const retry = () => {
        this.clearRetry(video);

        if (
          this.visibleVideos.has(video) &&
          !document.hidden
        ) {
          this.startVideo(video);
        }
      };

      state.readyHandler = retry;

      video.addEventListener('loadeddata', retry);
      video.addEventListener('canplay', retry);

      state.retryTimer = window.setTimeout(retry, 1800);
    }

    clearRetry(video) {
      const state = this.getVideoState(video);

      if (state.retryTimer) {
        window.clearTimeout(state.retryTimer);
        state.retryTimer = null;
      }

      if (state.readyHandler) {
        video.removeEventListener(
          'loadeddata',
          state.readyHandler
        );

        video.removeEventListener(
          'canplay',
          state.readyHandler
        );

        state.readyHandler = null;
      }
    }

    stopVideo(video) {
      this.clearRetry(video);

      video.autoplay = false;
      video.preload = 'metadata';
      video.controls = false;

      video.removeAttribute('autoplay');
      video.removeAttribute('data-autoplay-blocked');

      if (!video.paused) {
        video.pause();
      }

      const state = this.getVideoState(video);
      state.attempts = 0;
    }

    showManualControls(video) {
      this.clearRetry(video);

      video.autoplay = false;
      video.removeAttribute('autoplay');

      video.controls = true;
      video.setAttribute('data-autoplay-blocked', 'true');
    }

    getVideoState(video) {
      if (!this.videoStates.has(video)) {
        this.videoStates.set(video, {
          attempts: 0,
          retryTimer: null,
          readyHandler: null
        });
      }

      return this.videoStates.get(video);
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.visibleVideos.forEach(video => {
          this.stopVideo(video);
        });

        return;
      }

      this.visibleVideos.forEach(video => {
        this.startVideo(video);
      });
    }

    onPageShow() {
      if (document.hidden) return;

      this.visibleVideos.forEach(video => {
        this.startVideo(video);
      });
    }

    open(event, index, id) {
      document
        .querySelector(
          `shoppable-drawer.shoppable-drawer--${id}`
        )
        ?.openWithIndex(event, index);
    }
  }

  customElements.define(
    'shoppable-layout-slider',
    ShoppableLayoutSlider
  );
}